import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "@playbookai/api-clients";
import type { ResolvedPerson } from "@playbookai/types";
import { usePeopleSource } from "@playbookai/ui";
import { kpiAuthScopeKey, useKpiAuthScopeKey } from "./kpiAuthScope.js";
import { kpiKeys } from "./kpiQueryKeys.js";

/** A resolved person as the KPI surfaces show them. */
export interface KpiPersonName {
  readonly name: string;
  /** `false` covers suspended and removed alike. */
  readonly active: boolean;
}

/**
 * `PeopleSource.resolve`'s answer keyed by id — the one shape every
 * `kpiKeys.ownerNames` entry holds, whether this hook or the Owner filter
 * wrote it. An id the source does not know is absent.
 */
export function personNamesById(
  people: readonly ResolvedPerson[],
): Record<string, KpiPersonName> {
  return Object.fromEntries(
    people.map((person) => [
      person.id,
      { name: person.name, active: person.active },
    ]),
  );
}

/**
 * Batch-resolve owner ids → display names and the active flag via the host's
 * `PeopleSource.resolve`, for rendering `OwnerChip` in the list + detail.
 * Deduped + sorted so the query key is stable regardless of row order; skipped
 * when there are no owners to resolve, or no source (or no `resolve`) to ask —
 * callers then keep their unresolved fallback. The key embeds the reactive
 * host auth scope (PB-2941): names are identity data, and the shell's
 * QueryClient survives in-page auth transitions, so a scope change re-keys
 * (and re-resolves) rather than serving the previous tenant/session's names.
 */
export function useOwnerNames(ownerIds: ReadonlyArray<string | null>) {
  const config = useApiClient();
  const source = usePeopleSource();
  const scopeKey = useKpiAuthScopeKey();
  const ids = [
    ...new Set(ownerIds.filter((id): id is string => Boolean(id))),
  ].sort();
  return useQuery<Record<string, KpiPersonName>>({
    queryKey: kpiKeys.ownerNames(scopeKey, ids),
    queryFn: async () => {
      const names = personNamesById(await source!.resolve!(ids));
      if (
        kpiAuthScopeKey(config.authScope?.getSnapshot() ?? null) !== scopeKey
      ) {
        throw new DOMException(
          "KPI owner-name request crossed an auth-scope change.",
          "AbortError",
        );
      }
      return names;
    },
    enabled: ids.length > 0 && Boolean(source?.resolve),
    staleTime: 5 * 60_000,
  });
}
